/**
 * WeeklyContextMenu — updated to use Firebase via DataContext
 *
 * Replace: localStorage.setItem / localStorage.getItem
 * With:    persistMeals() from useData()
 *
 * All logic is identical; only the persist() implementation changes.
 */

import { useEffect } from "react";
import { useData } from '../contexts/DataContext';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getMealId(dayValue) {
  if (!dayValue) return null;
  if (typeof dayValue === 'string') return dayValue;
  return dayValue.id ?? null;
}

function getMealSides(dayValue) {
  if (!dayValue || typeof dayValue === 'string') return [];
  return dayValue.sides ?? [];
}

const SPECIAL_IDS = ['dine-out', 'delivery', 'date-night'];
const isSpecialRecipe = (id) => SPECIAL_IDS.includes(id);

export function WeeklyContextMenu({ x, y, recipe, weeklyMeals, targetServings, onClose }) {
  const { persistMeals } = useData();
  const isSpecial = isSpecialRecipe(recipe?.id);

  const scale = (targetServings && recipe?.servings && recipe.servings > 0)
    ? targetServings / recipe.servings
    : null;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSetMain = (day) => {
    const current = { ...weeklyMeals };
    const existingSides = getMealSides(current[day]);

    if (isSpecial) {
      current[day] = recipe.id;
    } else {
      current[day] = {
        id: recipe.id,
        sides: existingSides,
        ...(scale !== null && { scale }),
      };
    }
    persistMeals(current);
    onClose();
  };

  const handleAddSide = (day) => {
    const current = { ...weeklyMeals };
    const dayValue = current[day];
    const sides = getMealSides(dayValue);

    if (sides.some(s => s.id === recipe.id)) {
      onClose();
      return;
    }

    const newSide = { id: recipe.id, title: recipe.title };

    if (!dayValue) {
      current[day] = { id: null, sides: [newSide] };
    } else if (typeof dayValue === 'string') {
      current[day] = { id: dayValue, sides: [newSide] };
    } else {
      current[day] = { ...dayValue, sides: [...sides, newSide] };
    }

    persistMeals(current);
    onClose();
  };

  const menuWidth = isSpecial ? 200 : 260;
  const menuMaxHeight = 320;
  const left = Math.min(x, window.innerWidth - menuWidth - 8);
  const top  = Math.min(y, window.innerHeight - menuMaxHeight - 8);

  const getMainStatus = (day) => {
    const id = getMealId(weeklyMeals[day]);
    if (!id) return null;
    if (id === recipe?.id) return 'active';
    return 'replace';
  };

  const getSideStatus = (day) => {
    const sides = getMealSides(weeklyMeals[day]);
    return sides.some(s => s.id === recipe?.id) ? 'active' : null;
  };

  const colHeaderStyle = {
    fontSize: '0.7rem', fontWeight: 700, color: '#999',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    textAlign: 'center', padding: '0 6px',
  };

  const actionBtn = (status, color) => ({
    padding: '3px 8px',
    border: `1px solid ${status === 'active' ? color : '#ddd'}`,
    borderRadius: '4px',
    background: status === 'active' ? color : 'white',
    color: status === 'active' ? 'white' : status === 'replace' ? color : '#888',
    fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap', minWidth: '52px',
  });

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1999 }} />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top, left,
          background: 'white', border: '1px solid #ccc',
          borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
          zIndex: 2000, width: menuWidth, overflow: 'hidden', userSelect: 'none',
        }}
      >
        <div style={{
          padding: '8px 12px', fontWeight: 700, fontSize: '0.82rem',
          color: '#313131', borderBottom: '1px solid #eee',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {recipe?.title ? `"${recipe.title}"` : 'Add to plan'}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isSpecial ? '1fr auto' : '1fr auto auto',
          alignItems: 'center', padding: '5px 12px 3px',
          borderBottom: '1px solid #f0f0f0',
        }}>
          <span />
          <span style={colHeaderStyle}>Main</span>
          {!isSpecial && <span style={colHeaderStyle}>Side</span>}
        </div>

        <div style={{ maxHeight: 230, overflowY: 'auto' }}>
          {DAYS.map(day => {
            const mainStatus = getMainStatus(day);
            const sideStatus = getSideStatus(day);
            return (
              <div
                key={day}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isSpecial ? '1fr auto' : '1fr auto auto',
                  alignItems: 'center', gap: '6px', padding: '5px 12px',
                  borderBottom: '1px solid #f9f9f9',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <span style={{ fontSize: '0.88rem', color: '#313131', fontWeight: 500 }}>{day}</span>

                <button
                  onClick={() => handleSetMain(day)}
                  style={actionBtn(mainStatus, '#2196F3')}
                  title={mainStatus === 'replace' ? 'Replace current main meal' : mainStatus === 'active' ? 'Already set as main' : 'Set as main meal'}
                >
                  {mainStatus === 'replace' ? 'replace' : mainStatus === 'active' ? '✓ main' : 'main'}
                </button>

                {!isSpecial && (
                  <button
                    onClick={() => handleAddSide(day)}
                    disabled={sideStatus === 'active'}
                    style={{
                      ...actionBtn(sideStatus, '#4CAF50'),
                      opacity: sideStatus === 'active' ? 0.6 : 1,
                      cursor: sideStatus === 'active' ? 'default' : 'pointer',
                    }}
                    title={sideStatus === 'active' ? 'Already added as a side' : 'Add as a side dish'}
                  >
                    {sideStatus === 'active' ? '✓ side' : 'side'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div
          onClick={onClose}
          style={{
            padding: '7px 12px', cursor: 'pointer', borderTop: '1px solid #eee',
            color: '#888', fontWeight: 600, fontSize: '0.82rem', textAlign: 'center',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
        >
          Cancel
        </div>
      </div>
    </>
  );
}

export default WeeklyContextMenu;
